import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Download, Loader2, Plus, ReceiptText, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { SearchPicker } from "@/components/SearchPicker";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { STATUS_FILTERS, BUCKET_LABELS, formatDate, money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export default function Invoices() {
  const { currency } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [caseId, setCaseId] = useState(searchParams.get("case_id") || "all");
  const [firmId, setFirmId] = useState(searchParams.get("firm_id") || "all");
  const [forum, setForum] = useState("all");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [page, setPage] = useState(1);
  const bucket = searchParams.get("bucket") || "";
  const paidMonth = searchParams.get("paid_month") || "";
  const clientId = searchParams.get("client_id") || "";

  const clearDrill = () => {
    setSearchParams({}, { replace: true });
    setStatus("all");
    setFirmId("all");
    setCaseId("all");
    setQuery("");
  };

  useEffect(() => {
    (async () => {
      try {
        const [caseRes, firmRes] = await Promise.all([api.get("/cases"), api.get("/firms")]);
        setCases(caseRes.data);
        setFirms(firmRes.data);
      } catch (error) {
        toast.error(errorMessage(error, "Could not load filters"));
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (caseId !== "all") params.case_id = caseId;
      if (firmId !== "all") params.firm_id = firmId;
      if (forum !== "all") params.forum = forum;
      if (query.trim()) params.q = query.trim();
      if (bucket) params.bucket = bucket;
      if (paidMonth) params.paid_month = paidMonth;
      if (clientId) params.client_id = clientId;
      const { data } = await api.get("/invoices", { params });
      setRows(data);
      setPage(1);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load invoices"));
    } finally {
      setLoading(false);
    }
  }, [status, caseId, firmId, forum, query, bucket, paidMonth, clientId]);

  useEffect(() => {
    const timer = setTimeout(load, query ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  const forums = useMemo(
    () => [...new Set(cases.map((c) => c.forum).filter(Boolean))].sort(),
    [cases],
  );

  const pageRows = useMemo(
    () => rows.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [rows, page],
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.total += Number(r.total || 0);
          if (["sent", "overdue", "partial"].includes(r.display_status))
            acc.open += Number(r.balance_due ?? r.total ?? 0);
          return acc;
        },
        { total: 0, open: 0 },
      ),
    [rows],
  );

  const exportCsv = async () => {
    try {
      await downloadFile(
        `/exports/invoices.csv${status !== "all" ? `?status=${status}` : ""}`,
        "invoice_register.csv",
      );
      toast.success("CSV exported");
    } catch (error) {
      toast.error(errorMessage(error, "Export failed"));
    }
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Fee notes &amp; disbursements</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Invoices
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={exportCsv}
            data-testid="export-invoices-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
            Export CSV
          </button>
          <Link
            to="/invoices/new"
            data-testid="new-invoice-button"
            className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            New fee note
          </Link>
        </div>
      </header>

      <div className="surface p-4">
        {bucket || paidMonth || clientId ? (
          <div
            className="mb-3 flex flex-wrap items-center gap-3 border border-brand-terracotta/40 bg-[#FBF0EC] px-4 py-2.5"
            data-testid="invoices-drill-banner"
          >
            <p className="text-[12px] text-slate-700">
              Showing{" "}
              <span className="font-semibold">
                {bucket
                  ? `unpaid fee notes ${BUCKET_LABELS[bucket] || bucket}`
                  : paidMonth
                    ? `fee notes that received a payment in ${paidMonth}`
                    : "fee notes for the chosen client"}
              </span>
            </p>
            <button
              type="button"
              onClick={clearDrill}
              data-testid="clear-drill-button"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
            >
              <X className="h-3 w-3" strokeWidth={2.2} />
              Clear
            </button>
          </div>
        ) : null}
        <div className="grid gap-3 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Invoice no., case title, matter ref, solicitor…"
              className="rounded-sm pl-9"
              data-testid="invoice-search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm" data-testid="invoice-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SearchPicker
            items={[
              { id: "all", label: "All matters" },
              ...cases.map((c) => ({
                id: c.case_id,
                label: c.title,
                hint: [c.matter_ref, c.forum, c.client_name].filter(Boolean).join(" · "),
              })),
            ]}
            value={caseId}
            onSelect={(v) => setCaseId(v || "all")}
            placeholder="All matters"
            testId="invoice-case-filter"
            emptyLabel="No matter matches that text."
          />
          <SearchPicker
            items={[{ id: "all", label: "All forums" }, ...forums.map((f) => ({ id: f, label: f }))]}
            value={forum}
            onSelect={(v) => setForum(v || "all")}
            placeholder="All forums"
            testId="invoice-forum-filter"
            emptyLabel="No forum matches that text."
          />
        </div>
        <div className="mt-3 grid gap-3 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SearchPicker
              items={[
                { id: "all", label: "All law firms" },
                ...firms.map((f) => ({ id: f.firm_id, label: f.name, hint: f.city || "" })),
              ]}
              value={firmId}
              onSelect={(v) => setFirmId(v || "all")}
              placeholder="All law firms"
              testId="invoice-firm-filter"
              emptyLabel="No law firm matches that text."
            />
          </div>
          <div className="flex items-center gap-6 px-1 lg:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
              {rows.length} invoice(s)
            </p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
              Billed <span className="num text-slate-900">{money(totals.total, currency)}</span>
            </p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
              Open <span className="num text-slate-900">{money(totals.open, currency)}</span>
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="invoices-empty">
          <ReceiptText className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">No invoices match these filters.</p>
        </div>
      ) : (
        <div className="surface overflow-x-auto" data-testid="invoices-table">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Invoice", "Matter", "Billed to", "Issued", "Due", "Amount", "Status"].map((h) => (
                  <th
                    key={h}
                    className={`label-caps px-5 py-3 ${h === "Amount" ? "text-right" : ""}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map((row) => (
                <tr
                  key={row.invoice_id}
                  onClick={() => navigate(`/invoices/${row.invoice_id}`)}
                  data-testid={`invoice-row-${row.invoice_number}`}
                  className="cursor-pointer transition-colors duration-150 hover:bg-slate-50"
                >
                  <td className="px-5 py-4">
                    <p className="num text-[13px] text-slate-900">{row.invoice_number}</p>
                    <p className="num text-[10px] uppercase tracking-wider text-slate-400">
                      {row.matter_ref || "—"}
                    </p>
                  </td>
                  <td className="max-w-[280px] px-5 py-4">
                    <p className="truncate text-[13px] text-slate-800">{row.case_title}</p>
                    <p className="truncate text-[11px] text-slate-400">
                      {row.is_adhoc ? (
                        <span
                          className="border border-slate-300 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] text-slate-500"
                          data-testid={`invoice-adhoc-tag-${row.invoice_number}`}
                        >
                          One-off
                        </span>
                      ) : (
                        row.forum
                      )}
                    </p>
                  </td>
                  <td className="max-w-[220px] px-5 py-4">
                    <p className="truncate text-[13px] text-slate-800">{row.contact_name}</p>
                    <p className="truncate text-[11px] text-slate-400">
                      {row.firm_name || row.client_name || "Direct client"}
                    </p>
                  </td>
                  <td className="num px-5 py-4 text-[12px] text-slate-600">
                    {formatDate(row.issue_date)}
                  </td>
                  <td className="num px-5 py-4 text-[12px] text-slate-600">
                    {formatDate(row.due_date)}
                  </td>
                  <td className="num px-5 py-4 text-right text-[13px] text-slate-900">
                    {money(row.total, currency)}
                    {Number(row.amount_paid) > 0 && row.display_status !== "paid" ? (
                      <span className="block text-[10px] font-normal text-amber-600">
                        {money(row.balance_due ?? row.total - row.amount_paid, currency)} due
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge
                      status={row.display_status}
                      testId={`invoice-status-${row.invoice_number}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 ? (
        <Pager
          page={page}
          total={rows.length}
          onPage={(next) => {
            setPage(next);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          testId="invoices-pager"
        />
      ) : null}
    </div>
  );
}
